import React, { useState, useEffect, useContext, useRef, useCallback } from 'react';
import { Box, Tabs, Tab } from '@mui/material';
import { styled } from '@mui/material/styles';
import { WithdrawalContext } from '../contexts/WithdrawalContext';
import PayoutListing from '../components/PayoutListing';
import PayoutForm from '../components/PayoutForm';
import PayoutHistory from '../components/PayoutHistory';
import LoadingState from '../components/LoadingState';

const TAB_GRAB = 0;
const TAB_HISTORY = 1;

const CustomTabs = styled(Tabs)(() => ({
  borderRadius: '0.25rem',
  '& .MuiTab-root': {
    color: 'var(--secondary-color)',
    fontSize: '1rem',
    fontWeight: 'bold',
    '&.Mui-selected': {
      backgroundColor: 'var(--primary-color)',
      color: '#000',
    },
    '&:hover': {
      backgroundColor: '#333',
      color: '#fff',
      '&.Mui-selected': {
        backgroundColor: 'var(--primary-color)',
        color: '#000',
      },
    },
  },
}));

const Payout = () => {
  const [activeTab, setActiveTab] = useState(TAB_GRAB);
  const [activeWithdrawal, setActiveWithdrawal] = useState(null);
  const [isSyncing, setIsSyncing] = useState(true);
  const prevTabRef = useRef(TAB_GRAB);
  const hasInitializedRef = useRef(false);

  const {
    setWithdrawals,
    fetchWithdrawalById,
    fetchAvailableWithdrawals,
    setSelectedWithdrawalId,
    getProcessingWithdrawalId,
    setLoading,
  } = useContext(WithdrawalContext);

  const contextRef = useRef({});
  contextRef.current = {
    fetchWithdrawalById,
    fetchAvailableWithdrawals,
    getProcessingWithdrawalId,
    setSelectedWithdrawalId,
    setLoading,
  };

  const loadWithdrawal = useCallback(async (withdrawalId) => {
    const { fetchWithdrawalById } = contextRef.current;

    try {
      return await fetchWithdrawalById(withdrawalId);
    } catch (error) {
      console.error('Failed to fetch withdrawal:', error);
      return null;
    }
  }, []);

  const handleCancelStart = useCallback(() => {
    setIsSyncing(true);
    setActiveWithdrawal(null);
  }, []);

  const syncGrabTab = useCallback(async () => {
    const { getProcessingWithdrawalId, setSelectedWithdrawalId, setLoading, fetchAvailableWithdrawals } = contextRef.current;

    setIsSyncing(true);
    setLoading(true);

    try {
      const processingId = await getProcessingWithdrawalId();

      if (!processingId) {
        setSelectedWithdrawalId(null);
        setActiveWithdrawal(null);
        await fetchAvailableWithdrawals();
        return;
      }

      setSelectedWithdrawalId(processingId);
      const withdrawal = await loadWithdrawal(processingId);

      if (withdrawal) {
        setActiveWithdrawal(withdrawal);
      } else {
        setSelectedWithdrawalId(null);
        setActiveWithdrawal(null);
      }
    } finally {
      setLoading(false);
      setIsSyncing(false);
    }
  }, [loadWithdrawal]);

  const openTransferForm = useCallback(async (withdrawalId) => {
    const { setSelectedWithdrawalId } = contextRef.current;

    setIsSyncing(true);
    setSelectedWithdrawalId(withdrawalId);

    const withdrawal = await loadWithdrawal(withdrawalId);

    if (withdrawal) {
      setActiveWithdrawal(withdrawal);
    } else {
      setSelectedWithdrawalId(null);
      setActiveWithdrawal(null);
    }

    setIsSyncing(false);
  }, [loadWithdrawal]);

  useEffect(() => {
    const prevTab = prevTabRef.current;
    prevTabRef.current = activeTab;

    const isFirstLoad = !hasInitializedRef.current;
    const returnedToGrabTab = activeTab === TAB_GRAB && prevTab === TAB_HISTORY;

    if (!isFirstLoad && !returnedToGrabTab) return;

    hasInitializedRef.current = true;
    syncGrabTab();
  }, [activeTab, syncGrabTab]);

  const handleTabChange = (event, newValue) => {
    if (newValue === activeTab) return;
    setWithdrawals([]);

    // Set loading before paint so stale activeWithdrawal does not flash PayoutForm
    if (newValue === TAB_GRAB && activeTab === TAB_HISTORY) {
      setIsSyncing(true);
      setActiveWithdrawal(null);
    }

    setActiveTab(newValue);
  };

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', height: '100%', minHeight: 0 }}>
      <Box sx={{
          backgroundColor: 'var(--bg-color)',
          padding: 2,
          position: 'sticky',
          zIndex: 1000,
          flexShrink: 0,
        }}>
        <CustomTabs
          value={activeTab}
          onChange={handleTabChange}
          variant="fullWidth"
          TabIndicatorProps={{ style: { display: 'none' } }}
        >
          <Tab label="申购抢单" />
          <Tab label="申购记录" />
        </CustomTabs>
      </Box>

      <Box sx={{
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'stretch',
          margin: '0 16px',
          paddingBottom: 1.5,
          boxSizing: 'border-box',
          overflow: 'hidden',
          flex: 1,
          minHeight: 0,
        }}>
        {activeTab === TAB_GRAB && (
          <Box sx={{ display: 'flex', flexDirection: 'column', flex: 1, minHeight: 0 }}>
            {isSyncing ? (
              <LoadingState fullHeight />
            ) : activeWithdrawal ? (
              <PayoutForm
                withdrawal={activeWithdrawal}
                onBack={syncGrabTab}
                onCancelStart={handleCancelStart}
                onSubmit={() => {
                  setActiveWithdrawal(null);
                  setSelectedWithdrawalId(null);
                  setActiveTab(TAB_HISTORY);
                }}
              />
            ) : (
              <PayoutListing onBuy={openTransferForm} />
            )}
          </Box>
        )}

        {activeTab === TAB_HISTORY && (
          <Box sx={{ display: 'flex', flexDirection: 'column', flex: 1, minHeight: 0 }}>
            <PayoutHistory />
          </Box>
        )}
      </Box>
    </Box>
  );
};

export default Payout;
 